import React, {
  useContext, useEffect, useRef, useState, Suspense, lazy,
} from "react";
import Box from "@mui/material/Box";
import ClubFreeAccessAnnouncement from "./Components/ClubFreeAccessAnnouncement";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "./Components/Common/DrawerLayout/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";

import { Navigate, NavLink, useLocation, useNavigate } from "react-router-dom";

import { useAuthContext } from "./context/AuthContext";

import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { colors } from "./theme/colors";
import { fonts } from "./theme/fonts";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import Stack from "@mui/material/Stack";
import { Avatar, Chip } from "@mui/material";
import { getSeasonYear, isClubAdmin, isSuperAdmin } from "./utility/Utils";
import { useTranslation } from "./context/Internationalization";

import Loader from "./Components/Loader";
import TopBar from "./Components/TopBar/TopBar";
import AccountMenu from "./Components/AccountMenu/AccountMenu";
import SidebarClubHeader from "./Components/SidebarClubHeader";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { GET_USER } from "./graphql/userGraphql";
import { selectSessionClub } from "./auth/consoleSession";
import { clearCookies, clearStorages, getStorage, signOut } from "./auth/utils";
import { useInvitations } from "./context/InvitationContext";
import { GET_CLUB_TEAMS } from "./graphql/postGraphql";
import LogoutConfirmationModal from "./Components/LogoutConfirmationModal";
import ClubChooseConfirmationModal from "./Components/ClubChooseConfirmationModal";
import SmsLoginEmailPromptModal from "./Components/SmsLoginEmailPromptModal";
import MobileChangeModal from "./Components/MobileChangeModal";
import ContactPhoneModal from "./Components/ContactPhoneModal";
import UserPhotoModal from "./Components/UserPhotoModal";
import { useAppState } from "./context/AppContext";
import { AccountActionsContext } from "./context/AccountActionsContext";
import { SnackBarContext } from "./context/SnackBarContext";
import useGraphql from "./Hooks/useGraphql";
import useLog from "./Hooks/useLog";
import useClubBillingStatus from "./Hooks/useClubBillingStatus";
import { canViewClubBilling } from "./context/BillingGateContext";
import {
  shouldRestrictClubToBilling,
  shouldShowNavigationItemForBillingAccess,
} from "./pages/Billing/billingAccessPolicy";
import { canManageStaff } from "./pages/Staff/staffUtils";
import { localMap, USER_ROLES } from "./utility/Enum";
import {
  ACCOUNT_SETTINGS_ROUTE,
  BILLING_ROUTE,
  FILES_ROUTE,
  FORMS_ROUTE,
  HOME_ROUTE,
  MANAGER_HOME_ROUTE,
  NOTIFICATIONS_ROUTE,
  PLAYERS_ROUTE,
  POSTS_ROUTE,
  SCHEDULE_ROUTE,
  SHOP_ROUTE,
  STAFF_ROUTE,
  SUPER_ADMIN_CLUBS_ROUTE,
  SUPER_ADMIN_IMPERSONATE_ROUTE,
  SUPER_ADMIN_ROLES_ROUTE,
} from "./routes";

const { Grey, Base } = colors;

const {
  text: { md, sm },
} = fonts;

const drawerWidth = 312;
const ClubsDrawer = lazy(() =>
  import("./pages/SuperAdmin/ClubsTable/ClubsDrawer")
);

const MenuList = ({ menu, t, pathname, style, closeDrawerMobile }) => {
  return (
    <List style={style}>
      {menu(t).map((item, index) => {
        if (item.divider) {
          return (
            <div
              key={"divider" + index}
              style={{ borderTop: "1px solid #1F2831", margin: "16px" }}
            />
          );
        }

        const isActive = Boolean(
          (item.path && pathname.includes(item.path)) || item.drawerOpened
        );
        const NavigationIcon = item.icon;

        return (
          <ListItem key={index} disablePadding>
            <ListItemButton
              onClick={item.onClick ? () => {
                item.onClick();
                closeDrawerMobile?.();
              } : undefined}
              aria-expanded={item.onClick ? Boolean(item.drawerOpened) : undefined}
              style={{ paddingLeft: "24px", justifyContent: "space-between" }}
            >
              <div style={{ alignItems: "center", display: "flex" }}>
                <ListItemIcon style={{ minWidth: "38px" }}>
                  <NavigationIcon
                    sx={{
                      color: isActive ? Base.PlayerDex : Grey[500],
                      width: 24,
                      height: 24,
                    }}
                    aria-hidden="true"
                  />
                </ListItemIcon>

                {item.onClick ? (
                  <span style={isActive ? styles.selectedLink : styles.link}>
                    {item.label}
                  </span>
                ) : (
                  <NavLink
                    style={isActive ? styles.selectedLink : styles.link}
                    to={item.path}
                    onClick={closeDrawerMobile}
                  >
                    {item.label}
                  </NavLink>
                )}
              </div>
              {item.badge > 0 && (
                <Chip
                  style={{ height: 22 }}
                  label={item.badge}
                  color={"primary"}
                />
              )}
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};

const SidebarAccountButton = ({ user, onClick, open, t }) => {
  const contact = user?.email?.trim() || user?.mobile || "";
  const initials = `${(user?.firstName || "").substring(0, 1)}${(
    user?.lastName || ""
  ).substring(0, 1)}`;

  return (
    <div style={{ padding: "0 16px" }}>
      <List style={{ borderTop: "1px solid #1F2831" }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={onClick}
            aria-label={t("open_account_menu")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ width: "100%" }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <ListItemIcon style={{ minWidth: "38px" }}>
                  <Avatar src={user?.photo || undefined} alt="user photo">
                    {initials}
                  </Avatar>
                </ListItemIcon>
                <Stack direction="column" spacing={0} sx={{ minWidth: 0 }}>
                  <span style={styles.userName}>
                    {user?.firstName} {user?.lastName}
                  </span>
                  <span style={styles.userTitle} title={contact}>
                    {contact}
                  </span>
                </Stack>
              </Stack>
              <UnfoldMoreOutlinedIcon sx={{ color: Grey[400], flexShrink: 0 }} />
            </Stack>
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );
};

const AppShell = ({ window, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [chooseClubModal, setChooseClubModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [contactPhoneModalOpen, setContactPhoneModalOpen] = useState(false);
  const [userPhotoModalOpen, setUserPhotoModalOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [clubSettingsScope, setClubSettingsScope] = useState(null);
  const { t, switchLanguage, lang } = useTranslation();
  const auth = useAuthContext();
  const appState = useAppState();
  const { pathname } = useLocation();

  const user = auth?.authState?.user;
  const impersonating = Boolean(getStorage().getItem("impersonate"));
  const club = user?.club;
  const clubId = club?._id || "";
  const seasonStartYear = getSeasonYear();
  const season = `${seasonStartYear}/${String(seasonStartYear + 1).slice(-2)}`;
  const isUserSuperAdmin = user ? isSuperAdmin(user) : false;
  const isUserClubAdmin = user ? isClubAdmin(user) : false;
  const requestGeneration = useRef(0);
  const snackBar = useContext(SnackBarContext);
  const { invitations } = useInvitations();
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();
  const {
    state: billingGateState,
    loadBillingStatus,
    resetBilling,
  } = useClubBillingStatus();

  const captureRequestContext = () => {
    const generation = requestGeneration.current;
    const impersonatedUserId = () => {
      try { return JSON.parse(getStorage().getItem("impersonate"))?._id || null; }
      catch (_) { return null; }
    };
    const impersonation = impersonatedUserId();
    return () => generation === requestGeneration.current &&
      impersonation === impersonatedUserId();
  };

  const impersonateProc = () => {
    const impersonate = getStorage().getItem("impersonate");
    if (impersonate) {
      getStorage().setItem("impersonate", JSON.stringify(user));
    } else {
      getStorage().setItem("user", JSON.stringify(user));
    }
  };

  const getClubTeams = () => {
    if (!clubId) return;
    const isCurrent = captureRequestContext();

    axiosGraphQL({
      query: GET_CLUB_TEAMS,
      variables: {
        clubId,
        skip: 0,
        teamsLimit: 500,
      },
    })
      .then((response) => {
        if (!isCurrent()) return;
        const clubTeams = response.data.data.ClubTeams;
        appState.setClubTeams(clubTeams);
        Intercom("boot", {
          app_id: "f5ktqeup",
          user_id: user?._id,
          phone: user?.mobile,
          avatar: {
            type: "avatar",
            image_url:
              user?.photo ||
              `${`${(user?.firstName || "").substring(0, 1)}${(
                user?.lastName || ""
              ).substring(0, 1)}`}`,
          },
          company_id: clubId,
          company_name: user?.club?.name,
          company: {
            company_id: clubId,
            name: user?.club?.name,
            plan: user?.club?.isPremium ? "Premium" : "Basic",
            monthly_spend: user?.club?.monthlySpend,
            size: clubTeams?.length || 0,
            industry: clubTeams && clubTeams[0] && clubTeams[0].sport,
            company_website: user?.club?.website,
          },

          name: `${user?.firstName} ${user?.lastName}`, // Full name
          created_at: Date.now(), // Signup date as a Unix timestamp
        });

        Intercom("update");
        return response.data.data.ClubTeams;
      })
      .catch((error) => {
        if (!isCurrent()) return;
        errorLog(error, "AppShell", "getClubTeams", "GET_CLUB_TEAMS");
        snackBar.setOptions({
          alertMessage: error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
      });
  };

  const getUser = () => {
    const isCurrent = captureRequestContext();
    return axiosGraphQL({ query: GET_USER })
      .then(async (response) => {
        if (!isCurrent()) return;
        const user = response.data.data.me?.user;
        if (!user) {
          auth.logout();
          return;
        }

        const userLangDB = user.language;
        const userLangLS = getStorage().getItem("language");
        if (userLangDB !== userLangLS) {
          switchLanguage(userLangDB);
        }
        Object.assign(user, selectSessionClub(response.data.data.me, clubId));
        setSelectedClub(user.club);
        getStorage().setItem("activeClubId", String(user.club?._id || ""));

        auth.setAuthState({ user, token: getStorage().getItem("token") });

        const isValidRole =
          user.roles.includes(USER_ROLES.CLUB_ADMIN) ||
          user.roles.includes(USER_ROLES.SUPER_ADMIN);
        if (!isValidRole) {
          auth.logout();
        }
      })
      .catch((error) => {
        if (!isCurrent()) return;
        errorLog(error, "AppShell", "getUser", "GET_USER");
        snackBar.setOptions({
          alertMessage: error.message,
          alertSeverity: "error",
        });
        snackBar.setSnackbarOpen(true);
        throw error;
      });
  };

  useEffect(() => {
    let logoutTimer;
    setSelectedClub(user?.club || null);
    appState.setClubTeams([]);
    if (!clubId && !isUserSuperAdmin) {
      snackBar.setOptions({
        alertMessage: t("your_account_does_not_belong_to_a_club"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
      logoutTimer = setTimeout(() => {
        handleLogout();
      }, 4000);
    } else {
      if (clubId) getClubTeams();
      else if (typeof Intercom === "function") Intercom("shutdown");
      getUser().catch(() => undefined);
    }
    return () => {
      // Club switching and impersonation must invalidate old requests/timers.
      requestGeneration.current += 1;
      clearTimeout(logoutTimer);
    };
  }, [clubId, user?._id, isUserSuperAdmin, impersonating]);

  useEffect(() => {
    if (!clubId) {
      resetBilling();
      return;
    }

    loadBillingStatus(String(clubId));
  }, [clubId, loadBillingStatus, resetBilling]);

  const restrictNavigationToBilling = shouldRestrictClubToBilling(
    billingGateState,
    clubId,
    isUserSuperAdmin
  );

  const canOpenClubSettings = Boolean(
    clubId && (isUserClubAdmin || isUserSuperAdmin) && !restrictNavigationToBilling
  );
  const clubSettingsInScope =
    canOpenClubSettings &&
    clubSettingsScope?.clubId === clubId &&
    clubSettingsScope?.userId === user?._id &&
    clubSettingsScope?.impersonating === impersonating;
  const clubSettingsOpen = clubSettingsInScope && !clubSettingsScope.closing;

  const closeClubSettings = () => {
    setClubSettingsScope((scope) => scope && { ...scope, closing: true });
  };
  const handleClubSettingsExited = () => {
    setClubSettingsScope((scope) => (scope?.closing ? null : scope));
  };

  useEffect(() => {
    setClubSettingsScope(null);
  }, [clubId, user?._id, impersonating, canOpenClubSettings]);

  const menuLinks = (t) =>
    [
      {
        label: t("club"),
        path: HOME_ROUTE,
        icon: HomeOutlinedIcon,
      },
      {
        label: t("schedule_title"),
        path: SCHEDULE_ROUTE,
        icon: EventNoteOutlinedIcon,
      },
      isUserClubAdmin && {
        label: t("players_page_title"),
        path: PLAYERS_ROUTE,
        icon: GroupsOutlinedIcon,
      },
      {
        label: t("home"),
        path: POSTS_ROUTE,
        icon: CampaignOutlinedIcon,
      },
      {
        label: t("files"),
        path: FILES_ROUTE,
        icon: FolderOutlinedIcon,
      },
      {
        label: t("forms"),
        path: FORMS_ROUTE,
        icon: DescriptionOutlinedIcon,
      },
      {
        label: t("shop"),
        path: SHOP_ROUTE,
        icon: StorefrontOutlinedIcon,
      },
      canManageStaff(user) && {
        label: t("staff"),
        path: STAFF_ROUTE,
        icon: BadgeOutlinedIcon,
      },
    ]
      .filter(Boolean)
      .filter((item) =>
        shouldShowNavigationItemForBillingAccess(
          item,
          restrictNavigationToBilling
        )
      );

  const secondMenuLinks = (t) =>
    [
      canViewClubBilling(billingGateState, clubId) && {
        label: t("billing"),
        path: BILLING_ROUTE,
        icon: ReceiptLongOutlinedIcon,
      },
      canOpenClubSettings && {
        label: t("settings"),
        drawerOpened: clubSettingsOpen,
        onClick: () =>
          setClubSettingsScope({ clubId, userId: user._id, impersonating }),
        icon: SettingsOutlinedIcon,
      },
      clubId && !mobileOpen && {
        label: t("notifications"),
        path: NOTIFICATIONS_ROUTE,
        badge: invitations?.length,
        icon: NotificationsOutlinedIcon,
      },
    ]
      .filter(Boolean)
      .filter((item) =>
        shouldShowNavigationItemForBillingAccess(
          item,
          restrictNavigationToBilling
        )
      );

  let superAdminMenuLinks = (t) => {
    return [
      { divider: true },
      {
        label: t("clubs_admin"),
        path: SUPER_ADMIN_CLUBS_ROUTE,
        icon: ApartmentOutlinedIcon,
      },
      {
        label: `${t("search_user")}`,
        path: SUPER_ADMIN_IMPERSONATE_ROUTE,
        icon: PersonSearchOutlinedIcon,
      },
      {
        label: `${t("roles_admin")}`,
        path: SUPER_ADMIN_ROLES_ROUTE,
        icon: ManageAccountsOutlinedIcon,
      },
      { divider: true },
    ];
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    // shutdown Intercom
    if (Intercom) {
      Intercom("shutdown");
    }

    try {
      // Sign out from Firebase
      await signOut();

      // clear lS and SS
      clearStorages();

      // clear cookies
      clearCookies();

      // Perform logout via auth context
      auth.logout();

      // Close any open drawer
      closeDrawerMobile();
    } catch (error) {
      snackBar.setOptions({
        alertMessage: t("error_during_logout"),
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    if (
      user?.mobile &&
      !user?.email &&
      getStorage().getItem("lastLoginMethod") === "sms" &&
      Number(localStorage.getItem("emailPromptRemindAfter") || 0) <= Date.now()
    ) {
      setEmailModalOpen(true);
    }
  }, [user?._id, user?.email]);

  const navigate = useNavigate();

  const openAccountMenu = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const closeAccountMenu = () => {
    setAccountMenuAnchor(null);
  };

  const openAccountSettings = () => {
    closeDrawerMobile();
    navigate(ACCOUNT_SETTINGS_ROUTE);
  };

  const handleChangeSelectClub = async (selectedClubId) => {
    setSelectedClub(user.clubs.find((club) => club?._id === selectedClubId));
    setChooseClubModal(true);
  };

  const updateSelectedClub = async () => {
    const clubToBeSelected = user.clubs.find(
      (club) => club?._id === selectedClub?._id
    );
    user.club = clubToBeSelected;

    auth.setAuthState({
      user: {
        ...user,
        club: selectedClub,
      },
      token: getStorage().getItem("token"),
    });

    getStorage().setItem("activeClubId", String(selectedClub?._id || ""));

    impersonateProc();

    setChooseClubModal(false);
    closeDrawerMobile();
    navigate(MANAGER_HOME_ROUTE);
  };

  const onCancelSelectClub = () => {
    setSelectedClub(user.club);
    setChooseClubModal(false);
  };

  const closeDrawerMobile = () => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box>
      <SidebarClubHeader
        club={club}
        clubs={user?.clubs || []}
        selectedClubId={selectedClub?._id}
        season={season}
        onClubChange={handleChangeSelectClub}
      />
      <Divider sx={{ mb: 1, borderColor: Grey[800] }} />
      {clubId && <MenuList
        menu={menuLinks}
        t={t}
        pathname={pathname}
        closeDrawerMobile={closeDrawerMobile}
      />}
      {isUserSuperAdmin && (
        <MenuList
          menu={superAdminMenuLinks}
          t={t}
          pathname={pathname}
          closeDrawerMobile={closeDrawerMobile}
        />
      )}
    </Box>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const onLogout = () => {
    setLogoutModal(true);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={localMap[lang]}
    >
      <Box
        sx={{
          display: "flex",
          paddingBottom: "56px",
          paddingTop: { xs: "90px", lg: 0 },
        }}
      >
        <CssBaseline />
        <TopBar
          handleDrawerToggle={handleDrawerToggle}
          handleLogout={onLogout}
          invitations={invitations?.length}
          user={user}
        />
        <Box
          component="nav"
          sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}
          aria-label="navigator"
        >
          {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: "block", lg: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                backgroundColor: "#0D151A",
                ...(getStorage().getItem("impersonate") && {
                  marginTop: "24px",
                }),
                ...(getStorage().getItem("impersonate") && {
                  height: "calc(100% - 24px)",
                }),
              },
            }}
          >
            {drawer}
            <div style={{ height: "100%" }} />
            <MenuList
              menu={secondMenuLinks}
              t={t}
              pathname={pathname}
              closeDrawerMobile={closeDrawerMobile}
            />
            <ClubChooseConfirmationModal
              open={chooseClubModal}
              clubName={selectedClub?.name}
              onClose={onCancelSelectClub}
              onSave={updateSelectedClub}
            />
            <LogoutConfirmationModal
              logout={handleLogout}
              open={logoutModal}
              onClose={() => setLogoutModal(false)}
            />
            <SidebarAccountButton
              user={user}
              onClick={openAccountMenu}
              open={Boolean(accountMenuAnchor)}
              t={t}
            />
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", lg: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                backgroundColor: Base.Black,
              },
            }}
            open
          >
            {drawer}
            <div style={{ height: "100%" }} />
            <MenuList menu={secondMenuLinks} t={t} pathname={pathname} />
            <ClubChooseConfirmationModal
              open={chooseClubModal}
              clubName={selectedClub?.name}
              onClose={onCancelSelectClub}
              onSave={updateSelectedClub}
            />
            <LogoutConfirmationModal
              logout={handleLogout}
              open={logoutModal}
              onClose={() => setLogoutModal(false)}
            />
            <SidebarAccountButton
              user={user}
              onClick={openAccountMenu}
              open={Boolean(accountMenuAnchor)}
              t={t}
            />
          </Drawer>
        </Box>

        <AccountMenu
          anchorEl={accountMenuAnchor}
          open={Boolean(accountMenuAnchor)}
          onClose={closeAccountMenu}
          onOpenSettings={openAccountSettings}
          onLogout={onLogout}
          opensUpward
          showSettings={!impersonating}
        />

        {clubSettingsInScope && (
          <Suspense fallback={<Loader />}>
            <ClubsDrawer
              key={clubId}
              openDrawer={clubSettingsOpen}
              onExited={handleClubSettingsExited}
              selectedClub={club}
              title={t("club_settings")}
              onCloseDrawer={closeClubSettings}
              getClubs={() => globalThis.location.reload()}
            />
          </Suspense>
        )}

        {/* <Toolbar /> */}
        <AccountActionsContext.Provider
          value={{
            openEmailEditor: () => setEmailModalOpen(true),
            openLoginMobileEditor: () => {
              if (!user?.email) setMobileModalOpen(true);
            },
            openContactPhoneEditor: () => setContactPhoneModalOpen(true),
            openPhotoEditor: () => setUserPhotoModalOpen(true),
          }}
        >
          <Box sx={{ flex: "1 1 0%", minWidth: 0 }}>
            <ClubFreeAccessAnnouncement user={user} billingState={billingGateState} />
            <Suspense fallback={<Loader />}>{children}</Suspense>
          </Box>
        </AccountActionsContext.Provider>
        <SmsLoginEmailPromptModal
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          user={user}
          language={lang}
        />
        <MobileChangeModal
          open={mobileModalOpen}
          onClose={() => setMobileModalOpen(false)}
          user={user}
          axiosGraphQL={axiosGraphQL}
          onCompleted={({ user: refreshedUser, token }) =>
            auth.setAuthState({ user: refreshedUser, token })
          }
        />
        <ContactPhoneModal
          open={contactPhoneModalOpen}
          onClose={() => setContactPhoneModalOpen(false)}
          user={user}
          axiosGraphQL={axiosGraphQL}
          onCompleted={getUser}
        />
        <UserPhotoModal
          open={userPhotoModalOpen}
          onClose={() => setUserPhotoModalOpen(false)}
          axiosGraphQL={axiosGraphQL}
          onCompleted={getUser}
        />
      </Box>
    </LocalizationProvider>
  );
};

const styles = {
  link: {
    color: Grey[500],
    ...md.medium,
    textDecoration: "none",
  },
  selectedLink: {
    color: Base.PlayerDex,
    ...md.medium,
    textDecoration: "none",
  },
  userName: {
    ...sm.medium,
    color: Base.White,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  userTitle: {
    ...sm.normal,
    color: Grey[500],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

export default AppShell;
